import * as Sentry from '@sentry/react';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_DOMAIN } from 'consts';
import toUpper from 'lodash/toUpper';
import { security } from 'shared';
import { UserDashboardView, Watchlist } from 'shared/types';

export interface OilyticsAPIErrorResponse {
  type: string;
  message: string;
  code?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

export interface OptionChainColumnSet {
  strike: Array<number | null>;
  call_symbol?: Array<string | null>;
  call_last_price?: Array<number | null>;
  call_settle?: Array<number | null>;
  call_price?: Array<number | null>;
  call_close?: Array<number | null>;
  call_volume?: Array<number | null>;
  call_open_interest?: Array<number | null>;
  call_iv?: Array<number | null>;
  call_delta?: Array<number | null>;
  put_symbol?: Array<string | null>;
  put_last_price?: Array<number | null>;
  put_settle?: Array<number | null>;
  put_price?: Array<number | null>;
  put_close?: Array<number | null>;
  put_volume?: Array<number | null>;
  put_open_interest?: Array<number | null>;
  put_iv?: Array<number | null>;
  put_delta?: Array<number | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: Array<any> | undefined;
}

export interface OptionChainExpiration {
  expiration_date: string;
  product_symbol: string | null;
  columns: OptionChainColumnSet;
  last_future?: {
    close: number;
    symbol: string;
  } | null;
}

export interface OptionChainInstrument {
  leg_index: number;
  weight: number;
  formula_fragment: string;
  instrument_code: string;
  display_name?: string;
  tenor?: string;
  expirations: OptionChainExpiration[];
  focused_expiry?: string | null;
}

export interface OptionChainResponse {
  chart_type: 'option_chain';
  as_of: string;
  formula: string;
  instruments: OptionChainInstrument[];
}

interface TradeSymbolsParams {
  period?: string | null;
  tenor?: string | null;
  category?: string | null;
  page: number;
  q?: string | null;
}

export default class OilyticsClient {
  token: string;
  httpClient: AxiosInstance;

  constructor (token: string) {
    this.token = token;
    this.httpClient = axios.create({
      baseURL: `${API_DOMAIN}/api`,
      timeout: 30000,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      validateStatus: (status) => status === 200,
    });
    this.httpClient.interceptors.response.use((r) => r, (err) => {
      // response is undefined for network-level failures (timeout, reset,
      // offline) — fall back to the axios error code so those reject with
      // the real error instead of a TypeError from this interceptor
      const { config, response, code } = err as { config: AxiosRequestConfig, response?: AxiosResponse, code?: string };
      const msg = `${toUpper(config?.method)} ${config?.url} ${response?.status ?? code ?? 'no-response'} (${err.message})`;
      console.log('----', msg);
      // 404s are the expected answer for stale URLs to deleted/unshared
      // resources — not worth an error event
      if (response?.status !== 404) {
        Sentry.captureMessage(msg, { level: 'error', extra: { response } });
      }
      return Promise.reject(err);
    });
  }

  static async create () {
    // Print mode: when the page sets `window.__renderToken` (the headless
    // Chromium PDF renderer does this via PrintArtifactPage), use that
    // short-lived render token instead of calling Auth0 — there's no
    // logged-in user inside the headless browser.
    const renderToken = (window as unknown as { __renderToken?: string }).__renderToken;
    if (renderToken) return new OilyticsClient(renderToken);
    const token = await security.getAccessTokenSilently()();
    return new OilyticsClient(token);
  }

  async getDashboards (tagId?: number) {
    const queryData = !tagId || tagId === 0 ? {} : { tags: [tagId] };
    return this.httpClient?.get('/dashboards', { params: queryData });
  }

  // GET /api/dashboard/ (trailing slash) — NOT the same as getDashboards
  // above. The two backend endpoints differ in filtering and shape:
  //   /api/dashboard/  → favorite-or-private dashboards with user=1 (public
  //                      without it); rows carry `favorite` and a slugified
  //                      `url`, but no report_id/iframe_src; uncached.
  //   /api/dashboards  → everything the user can access, tags[]-filterable;
  //                      marshmallow-serialized with `is_favorite`,
  //                      report_id/iframe_src; cached 1h server-side.
  // nav-apps and Shiptracking need the former's url/favorite fields; Home
  // needs the latter's tag filtering and embed fields.
  // TODO: consolidate into a single dashboards list endpoint on the backend
  // (see the 2020 TODO on _list_dashboards in apps/api views/api.py), then
  // collapse getDashboardList/useDashboardsList into getDashboards.
  async getDashboardList (isUserDashboard = false) {
    return this.httpClient?.get('/dashboard/', {
      params: isUserDashboard ? { user: 1 } : {},
    });
  }

  async getDashboardTags () {
    return this.httpClient?.get('/dashboard_tags');
  }

  async getReports () {
    return this.httpClient?.get('/reports');
  }

  async generateEmbedToken (reportId: string) {
    return this.httpClient?.post('/generate-embed-token', { report_id: reportId });
  }

  async getCurrentUser () {
    return this.httpClient?.get('/user/current');
  }

  async setUserTimezone (timezone: string) {
    return this.httpClient?.put('/user/timezone', { timezone });
  }

  // Bearer-authed OpenAPI spec for the in-app Data API docs viewer, tailored to
  // the user's PL tier. Distinct from the public Basic-auth /api/openapi.json
  // that partners hit with curl.
  async getDataApiSpec () {
    return this.httpClient?.get('/data-api/openapi.json');
  }

  async getDashboard (id: number | string) {
    return this.httpClient?.get(`/dashboard/${id}`, { timeout: 10000 });
  }

  async favoriteDashboard (dashboardId: number) {
    return this.httpClient?.get(`/dashboards/${dashboardId}/take`);
  }

  async unfavoriteDashboard (dashboardId: number) {
    return this.httpClient?.delete(`/dashboard/${dashboardId}`);
  }

  async getDailyReports () {
    return this.httpClient?.get('/daily-reports');
  }

  async getDailyReport (reportId: number) {
    return this.httpClient?.get(`/daily-reports/${reportId}`);
  }

  async getUserDashboardViews (dashboardId: number) {
    return this.httpClient?.get(`/user/dashboards/${dashboardId}/views`);
  }

  async createUserDashboardView (dashboardId: number, data: UserDashboardView) {
    return this.httpClient?.post(`/user/dashboards/${dashboardId}/views`, data);
  }

  async deleteUserDashboardView (dashboardId: number, userDashboardViewId: number) {
    return this.httpClient?.delete(`/user/dashboards/${dashboardId}/views/${userDashboardViewId}`);
  }

  async getWatchlists () {
    return this.httpClient?.get('/user/watchlists');
  }

  async getPublicWatchlists () {
    return this.httpClient?.get('/public/watchlists');
  }

  async getPublicWatchlist (watchlistId: string) {
    return this.httpClient?.get(`/public/watchlists/${watchlistId}?include_trades=true`);
  }

  async getPublicTradeWithChartData (watchlistId: number | string, tradeId: number | string, chartType = 'timeline', frequency?: 'D' | 'W' | 'M', periods?: number, dates?: string[]) {
    const params: any = { chart_type: chartType };
    if (frequency && periods) {
      params.frequency = frequency;
      params.periods = periods;
    }
    if (dates && dates.length > 0) {
      params.date = dates;
    }
    return this.httpClient?.get(`/public/watchlists/${watchlistId}/trades/${tradeId}`, {
      params,
    });
  }

  async getWatchlist (watchlistId: string, include_trades = true) {
    return this.httpClient?.get(`/user/watchlists/${watchlistId}?include_trades=${include_trades}`);
  }

  async createWatchlist (watchlist: Partial<Watchlist>) {
    return this.httpClient?.post('/user/watchlists', watchlist);
  }

  async deleteWatchlist (watchlistId: string) {
    return this.httpClient?.delete(`/user/watchlists/${watchlistId}`);
  }

  async editWatchlist (watchlistId: string, watchlist: Partial<Watchlist>) {
    return this.httpClient?.patch(`/user/watchlists/${watchlistId}`, watchlist);
  }

  async copyWatchlist (watchlistId: string) {
    return this.httpClient?.post(`/watchlists/${watchlistId}/copy`, {});
  }

  async getTradeCategories () {
    return this.httpClient?.get('/trades/categories');
  }

  async getTradeSymbols (params: TradeSymbolsParams) {
    return this.httpClient?.get('/trades/symbols', { params });
  }

  async getTradePreview (formula: string, chartType = 'timeline', frequency?: 'D' | 'W' | 'M', periods?: number, dates?: string[], extraParams?: Record<string, string | number | boolean | undefined>) {
    const params: any = {
      formula,
      chart_type: chartType,
    };
    if (frequency && periods) {
      params.frequency = frequency;
      params.periods = periods;
    }
    if (dates && dates.length > 0) {
      params.date = dates;
    }
    if (extraParams) {
      for (const [key, value] of Object.entries(extraParams)) {
        if (value !== undefined) {
          params[key] = value;
        }
      }
    }
    return this.httpClient?.get('/trades', {
      params,
    });
  }

  async addToWatchlist (watchlistId: string, formula: string, short_name?: string, unit_override?: string) {
    const payload: any = { formula };
    if (short_name) {
      payload.short_name = short_name;
    }
    if (unit_override) {
      payload.unit_override = unit_override;
    }
    return this.httpClient?.post(`/user/watchlists/${watchlistId}/symbols`, payload);
  }

  async getTradeWithChartData (watchlistId: number | string, tradeId: number | string, chartType = 'timeline', frequency?: 'D' | 'W' | 'M', periods?: number, dates?: string[]) {
    const params: any = { chart_type: chartType };
    if (frequency && periods) {
      params.frequency = frequency;
      params.periods = periods;
    }
    if (dates && dates.length > 0) {
      params.date = dates;
    }
    return this.httpClient?.get(`/user/watchlists/${watchlistId}/trades/${tradeId}`, {
      params,
    });
  }

  async updateTrade (watchlistId: string, tradeId: string, data: { formula?: string; short_name?: string; unit_override?: string }) {
    return this.httpClient?.patch(`/user/watchlists/${watchlistId}/trades/${tradeId}`, data);
  }

  async deleteTrade (watchlistId: string, tradeId: string) {
    return this.httpClient?.delete(`/user/watchlists/${watchlistId}/trades/${tradeId}`);
  }

  async addGroupToWatchlist (watchlistId: string, group: { name: string; index?: number }) {
    return this.httpClient?.post(`/user/watchlists/${watchlistId}/groups`, group);
  }

  async updateGroupInWatchlist (watchlistId: string, groupId: number, data: { name?: string; index?: number }) {
    return this.httpClient?.patch(`/user/watchlists/${watchlistId}/groups/${groupId}`, data);
  }

  async deleteGroupFromWatchlist (watchlistId: string, groupId: number) {
    return this.httpClient?.delete(`/user/watchlists/${watchlistId}/groups/${groupId}`);
  }

  async reorderTrade (watchlistId: string, tradeId: string, data: { index?: number; group_id?: number | null }) {
    return this.httpClient?.patch(`/user/watchlists/${watchlistId}/trades/${tradeId}/reorder`, data);
  }

  async reorderGroup (watchlistId: string, groupId: number, data: { index?: number }) {
    return this.httpClient?.patch(`/user/watchlists/${watchlistId}/groups/${groupId}/reorder`, data);
  }

  async getIntradayLatest (watchlistId: string, isPublic = false) {
    const prefix = isPublic ? '/public' : '/user';
    return this.httpClient?.get(`${prefix}/watchlists/${watchlistId}/intraday-latest`);
  }

  async postIntradayLatest (
    watchlistId: string,
    historicFormulas: Array<{ id: string; formula: string }>,
    isPublic = false
  ) {
    const prefix = isPublic ? '/public' : '/user';
    return this.httpClient?.post(`${prefix}/watchlists/${watchlistId}/intraday-latest`, {
      historic_formulas: historicFormulas,
    });
  }

  async postForwardCurves (watchlistId: string, tradeIds: string[], isPublic = false) {
    const prefix = isPublic ? '/public' : '/user';
    return this.httpClient?.post(`${prefix}/watchlists/${watchlistId}/forward-curves`, {
      trade_ids: tradeIds,
    });
  }

  async getLiveForwardCurve (watchlistId: number | string, tradeId: number | string, isPublic = false) {
    const prefix = isPublic ? '/public' : '/user';
    return this.httpClient?.get(`${prefix}/watchlists/${watchlistId}/trades/${tradeId}/live-forward-curve`);
  }

  async getPreviewLiveForwardCurve (formula: string) {
    return this.httpClient?.get('/user/trades/preview/live-forward-curve', {
      params: { formula },
    });
  }

  async getPricingSheet (
    watchlistId: string,
    isPublic = false,
    depth?: { months: number; quarters: number; years: number },
    mode?: 'intraday' | 'eod'
  ) {
    const prefix = isPublic ? '/public' : '/user';
    return this.httpClient?.get(`${prefix}/watchlists/${watchlistId}/pricing-sheet`, {
      params: { ...depth, mode },
    });
  }

  async copyTrade (watchlistId: string, tradeId: string, targetWatchlistId: string) {
    return this.httpClient?.post(`/user/watchlists/${watchlistId}/trades/${tradeId}/copy`, {
      target_watchlist_id: targetWatchlistId,
    });
  }

  async getChartStorageList (type: string, symbol?: string) {
    const params: Record<string, string> = { type };
    if (symbol) params.symbol = symbol;
    return this.httpClient?.get('/user/chart-storage', { params });
  }

  async getChartStorageItem (id: number) {
    return this.httpClient?.get(`/user/chart-storage/${id}`);
  }

  async saveChartStorageItem (data: {
    id?: number;
    type: string;
    name: string;
    symbol?: string;
    resolution?: string;
    content: string;
  }) {
    return this.httpClient?.post('/user/chart-storage', data);
  }

  async deleteChartStorageItem (id: number) {
    return this.httpClient?.delete(`/user/chart-storage/${id}`);
  }

  async getSavedViewsList (page: string) {
    return this.httpClient?.get('/user/saved-views', { params: { page } });
  }

  async getSavedViewItem (id: number) {
    return this.httpClient?.get(`/user/saved-views/${id}`);
  }

  async saveSavedViewItem (data: { id?: number; page: string; name: string; content: string }) {
    return this.httpClient?.post('/user/saved-views', data);
  }

  async deleteSavedViewItem (id: number) {
    return this.httpClient?.delete(`/user/saved-views/${id}`);
  }
}
